import { forwardRef, type ImgHTMLAttributes } from 'react';
import { useTheme } from '@/app/providers/useTheme';
import { cn } from '@/shared/lib/cn';
import markOnDarkUrl from '@/shared/assets/brand/workerize-mark-ondark.svg';
import markOnLightUrl from '@/shared/assets/brand/workerize-mark-onlight.svg';

export interface WorkerizeMarkThemedProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  size?: number;
  title?: string;
}

/**
 * Theme-aware Workerize mark. Picks the on-light (Ink) or on-dark (Lime)
 * artwork from the active `[data-theme]`, so the mark stays legible on a
 * surface that flips with the theme (e.g. the mobile top bar on `--surface-base`).
 * Must be rendered under `<ThemeProvider>`. For an always-dark surface (the icon
 * rail's ink band) use {@link WorkerizeMark}, which is lime and theme-stable.
 *
 * Like {@link WorkerizeMark}, the artwork is 5:6 — `size` drives the HEIGHT and
 * the width follows the intrinsic ratio.
 */
export const WorkerizeMarkThemed = forwardRef<HTMLImageElement, WorkerizeMarkThemedProps>(
  function WorkerizeMarkThemed(
    { size = 28, title = 'Workerize', className, ...rest },
    ref,
  ) {
    const { theme } = useTheme();
    return (
      <img
        ref={ref}
        src={theme === 'dark' ? markOnDarkUrl : markOnLightUrl}
        alt={title}
        height={size}
        className={cn('inline-block w-auto', className)}
        style={{ height: `${size}px` }}
        {...rest}
      />
    );
  },
);

export default WorkerizeMarkThemed;
